/**
 * The offline queue (T07-33 ↔ T07-17 `POST /pos/sync/batch`): every cash sale made while offline
 * is one operation in Dexie `opQueue` with its own `idempotencyKey` (UUID v7 — time ordered). It
 * survives closing the app / a restart and is removed **only** when the server answered `ok`,
 * `duplicate`, `conflict` or `rejected` for it (`pending` stays).
 *
 * The temporary receipt number `T-<register><device>-<n>` counts per tablet in Dexie `settings`.
 */
import type { OpSummary, PosDatabase, QueuedOp } from "@/lib/db";
import { readSetting, writeSetting } from "@/lib/db";
import { newIdempotencyKey } from "@/lib/idempotency";

export const TEMP_SEQ_KEY = "offlineReceiptSeq";
export const OLD_OP_MS = 24 * 3_600_000;

export interface OfflineSaleLine {
  variantId: string;
  code: string | null;
  unitId?: string | null;
  qty: number;
  /** 税込 price shown on the tablet (from the offline copy). */
  unitPrice: number;
  /** When the copy said this card was free (one-of-a-kind). */
  availableAt: string | null;
  hasExternalListing: boolean;
  title: string;
}

export interface OfflineSaleInput {
  registerId: string;
  tempReceiptNo: string;
  lines: OfflineSaleLine[];
  total: number;
  tendered: number;
  snapshotVersion: string | null;
  snapshotAt: string | null;
  shiftId: string | null;
  staffId: string | null;
  staffName: string | null;
  locale: string;
  paperWidth: 58 | 80;
  at?: Date;
}

/** `2026-09-28T03:12:45+09:00` — the time with the shop's zone (the contract wants a zone). */
export function jstIso(at: Date): string {
  const jst = new Date(at.getTime() + 9 * 3_600_000);
  return `${jst.toISOString().slice(0, 19)}+09:00`;
}

/** The short tablet part of `T-…`: register code + 2 characters of the device id. */
export function tempPrefix(registerCode: string, deviceId: string): string {
  const register = registerCode.replace(/[^A-Za-z0-9]/g, "").slice(0, 10) || "R";
  const device = deviceId.replace(/[^A-Za-z0-9]/g, "").slice(-2);
  return `${register}${device}`.slice(0, 12);
}

export async function nextTempReceiptNo(db: PosDatabase, prefix: string): Promise<string> {
  return db.transaction("rw", db.settings, async () => {
    const next = (await readSetting<number>(db, TEMP_SEQ_KEY, 0)) + 1;
    await writeSetting(db, TEMP_SEQ_KEY, next);
    return `T-${prefix}-${String(next)}`;
  });
}

export async function enqueueSale(db: PosDatabase, sale: OfflineSaleInput): Promise<QueuedOp> {
  const at = sale.at ?? new Date();
  const summary: OpSummary = {
    tempReceiptNo: sale.tempReceiptNo,
    total: sale.total,
    tendered: sale.tendered,
    items: sale.lines.map((l) => ({ title: l.title, qty: l.qty, price: l.unitPrice })),
    staffName: sale.staffName,
  };
  const op: QueuedOp = {
    kind: "sale_cash",
    idempotencyKey: newIdempotencyKey(),
    registerId: sale.registerId,
    createdAt: at.getTime(),
    clientTime: jstIso(at),
    attempts: 0,
    summary,
    payload: {
      tempReceiptNo: sale.tempReceiptNo,
      snapshotVersion: sale.snapshotVersion,
      snapshotAt: sale.snapshotAt,
      shiftId: sale.shiftId,
      staffId: sale.staffId,
      staffName: sale.staffName,
      lines: sale.lines.map((l) => ({
        variantId: l.variantId,
        ...(l.code ? { code: l.code } : {}),
        ...(l.unitId ? { unitId: l.unitId } : {}),
        qty: l.qty,
        unitPrice: l.unitPrice,
        ...(l.availableAt ? { availableAt: l.availableAt } : {}),
        hasExternalListing: l.hasExternalListing,
      })),
      total: sale.total,
      cash: { tendered: sale.tendered },
      locale: sale.locale,
      paperWidth: sale.paperWidth,
    },
  };
  const id = await db.opQueue.add(op);
  return { ...op, id };
}

export async function queuedOps(db: PosDatabase, limit?: number): Promise<QueuedOp[]> {
  const all = db.opQueue.orderBy("createdAt");
  return limit ? all.limit(limit).toArray() : all.toArray();
}

export function isOld(op: Pick<QueuedOp, "createdAt">, now = Date.now()): boolean {
  return now - op.createdAt > OLD_OP_MS;
}
